// eslint-disable-next-line max-classes-per-file
import lowerFirst from 'lodash/lowerFirst';
import snakeCase from 'lodash/snakeCase';

/**
 * Исключения для исключения проеба в написании названия набора разрешений
 *
 * @export
 * @class PermissionSetDoesNotExist
 * @extends {Error}
 */
export class PermissionSetDoesNotExist extends Error {
  name = 'PermissionSetDoesNotExist';

  constructor(setName: string) {
    super(`Trying to get not existing permission set '${setName}'.`);
  }
}

/**
 * Исключения для исключения проеба в написании названия разрешения
 *
 * @export
 * @class PermissionDoesNotExist
 * @extends {Error}
 */
export class PermissionDoesNotExist extends Error {
  name = 'PermissionDoesNotExist';

  constructor(setName: string, permission: string) {
    super(`Trying to get not existing permission '${permission}' from set '${setName}'.`);
  }
}

/**
 * Родительский класс для всех наборов разрешений
 * имеет встроенную защиту от запроса несуществующих разрешений
 *
 * @export
 * @abstract
 * @class PermissionSet
 */
export abstract class PermissionSet {
  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const origin = this;
    // eslint-disable-next-line no-constructor-return
    return new Proxy(this, {
      get: (target, name) => {
        if (origin.getExistingPermissions().includes(String(name))) {
          return target[String(name)];
        }
        throw new PermissionDoesNotExist(origin.getPermissionsSetName(), String(name));
      },
    });
  }

  /**
   * Генерирует wildcard вида `название_набора_разрешений.*`
   *
   * @readonly
   * @memberof PermissionSet
   */
  public get any(): string {
    return `${this.getPermissionsSetNameSnakeCase()}.*`;
  }

  /**
   * Генерирует набор разрешений для действия (CUD)
   *
   * @readonly
   * @memberof PermissionSet
   */
  public get actions(): Array<string> {
    return [
      `${this.getPermissionsSetNameSnakeCase()}.create`,
      `${this.getPermissionsSetNameSnakeCase()}.update`,
      `${this.getPermissionsSetNameSnakeCase()}.delete`,
    ];
  }

  /**
   * Возвращает массив всех разрешений
   * этого 'набора'
   *
   * @readonly
   * @memberof PermissionSet
   */
  public get all(): Array<string> {
    return Object.values(this);
  }

  /**
   * Получаем назвагите 'набора' разрешений,
   * конвенционально это названия с бекэнда только в camelCase
   * по факту же это названеи класса со строчным первым символом
   *
   * @returns {string}
   * @memberof PermissionSet
   */
  protected getPermissionsSetName(): string {
    return lowerFirst(this.constructor.name);
  }

  /**
   * Переводим в snake case
   *
   * @returns {string}
   * @memberof PermissionSet
   */
  protected getPermissionsSetNameSnakeCase(): string {
    return snakeCase(this.constructor.name);
  }

  /**
   * Получаем массив возможных разрешений для 'набора' разрешений
   *
   * @returns {Array<string>}
   * @memberof PermissionSet
   */
  protected getExistingPermissions(): Array<string> {
    return [...Object.getOwnPropertyNames(this), 'any', 'all', 'actions'];
  }
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для ролей
 *
 * @export
 * @class role
 * @extends {PermissionSet}
 */
export class Role extends PermissionSet {
  /**
   * Просмотр информации о роли
   * @memberof role
   */
  public readonly list = 'role.list';

  /**
   * Просмотр разрешений для роли
   * @memberof role
   */
  public readonly permissionsList = 'role.permissions_list';

  /**
   * Создание роли
   * @memberof role
   */
  public readonly create = 'role.create';

  /**
   * Обновление информации о роли
   * @memberof role
   */
  public readonly update = 'role.update';

  /**
   * Удаление роли
   * @memberof role
   */
  public readonly delete = 'role.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для пользователей
 *
 * @export
 * @class user
 * @extends {PermissionSet}
 */
export class User extends PermissionSet {
  /**
   * Просмотр информации о пользователе
   * @memberof user
   */
  public readonly list = 'user.list';

  /**
   * Доступ к справочнику получателей мер поддержки.
   * В т.ч. доступ к просмотру его профилей, заявок и проектов
   * @type {string}
   */
  public readonly listAll = 'user.list_all';

  /**
   * Создание пользователя
   * @memberof user
   */
  public readonly create = 'user.create';

  /**
   * Обновление информации о пользователе
   * @memberof user
   */
  public readonly update = 'user.update';

  /**
   * Удаление пользователя
   * @memberof user
   */
  public readonly delete = 'user.delete';

  /**
   * Верификация пользователя
   * Создание операторов в системе (всех ролей, за исключением ролей: "Пользователь, Администратор")
   * @memberof User
   */
  public readonly createOperators = 'user.create_operators';

  /**
   * Просмотр информации об истории посещений пользователя
   *
   * @memberOf User
   */
  public readonly viewHistory = 'user.view_history';

  /**
   * Просмотр детальной информации о сотруднике департамента
   * @type {string}
   */
  public readonly info = 'user.info';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для департаментов
 *
 * @export
 * @class department
 * @extends {PermissionSet}
 */
export class Department extends PermissionSet {
  /**
   * Просмотр информации о департаменте
   * @memberof department
   */
  public readonly list = 'department.list';

  public readonly listBrief = 'department.list_brief';

  /**
   * Создание департамента
   * @memberof department
   */
  public readonly create = 'department.create';

  /**
   * Обновление информации о департаменте
   * @memberof department
   */
  public readonly update = 'department.update';

  /**
   * Удаление департамента
   * @memberof department
   */
  public readonly delete = 'department.delete';

  /**
   * Просмотр детальной информации о департаменте
   * @type {string}
   */
  public readonly info = 'department.info';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для файлов
 *
 * @export
 * @class file
 * @extends {PermissionSet}
 */
export class File extends PermissionSet {
  /**
   * Просмотр (скачивание) файла
   * @memberof file
   */
  public readonly list = 'file.list';

  /**
   * Доступ к файлам всех пользователей
   * @memberof file
   */
  public readonly watch = 'file.watch';

  /**
   * Загрузка файла
   * @memberof file
   */
  public readonly create = 'file.create';

  /**
   * Создание файлов от имени пользователя
   * @memberof file
   */
  public readonly createForAll = 'file.create_for_all';

  /**
   * Замена файла
   * @memberof file
   */
  public readonly update = 'file.update';

  /**
   * Удаление файла
   * @memberof file
   */
  public readonly delete = 'file.delete';

  /**
   * История
   * @memberof file
   */
  public readonly history = 'file.history';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для справочников
 *
 * @export
 * @class catalog
 * @extends {PermissionSet}
 */
export class Catalog extends PermissionSet {
  /**
   * Просмотр информации о справочнике
   * @memberof catalog
   */
  public readonly list = 'catalog.list';

  /**
   * Создание справочника
   * @memberof catalog
   */
  public readonly create = 'catalog.create';

  /**
   * Обновление информации о справочнике
   * @memberof catalog
   */
  public readonly update = 'catalog.update';

  /**
   * Удаление справочника
   * @memberof catalog
   */
  public readonly delete = 'catalog.delete';
}

/**
 * Набор разрешений для шаблонов сообщений
 * Backend группа: MessageTemplate
 *
 * @export
 * @class MessageTemplate
 * @extends {PermissionSet}
 */
export class MessageTemplate extends PermissionSet {
  /**
   * Просмотр информации шаблонов сообщений
   * @memberof MessageTemplate
   */
  public readonly list = 'message_template.list';

  /**
   * Создание шаблонов сообщений
   * @memberof MessageTemplate
   */
  public readonly create = 'message_template.create';

  /**
   * Обновление шаблонов сообщений
   * @memberof MessageTemplate
   */
  public readonly update = 'message_template.update';

  /**
   * Удаление шаблонов сообщений
   * @memberof MessageTemplate
   */
  public readonly delete = 'message_template.delete';

  /**
   * Генерация текстов сообщений по шаблону
   * @memberof MessageTemplate
   */
  public readonly aplay = 'message_template.aplay';

  /**
   * Генерация текстов сообщений по шаблону
   * @memberof MessageTemplate
   */
  public readonly forAllDepartment = 'message_template.for_all_department';
}

/**
 * Набор разрешений для активностей физ лиц
 * Backend группа: activities
 *
 * @export
 * @class activity
 * @extends {PermissionSet}
 */
export class Activities extends PermissionSet {
  /**
   * Просмотр информации об активностях физ лиц
   * @memberof activity
   */
  public readonly list = 'activities.list';

  /**
   * Создание активности физ лиц
   * @memberof activity
   */
  public readonly create = 'activities.create';

  /**
   * Обновление активности физ лиц
   * @memberof activity
   */
  public readonly update = 'activities.update';

  /**
   * Удаление активности физ лиц
   * @memberof activity
   */
  public readonly delete = 'activities.delete';
}

/**
 * Набор разрешений "Экономические отношения физ.лиц" в модуле ТТП"
 *
 * @extends {PermissionSet}
 */
export class TtpEconomicAgreement extends PermissionSet {
  /**
   * Просмотр информации
   */
  public readonly list = 'ttp_economic_agreement.list';

  /**
   * Создание
   */
  public readonly create = 'ttp_economic_agreement.create';

  /**
   * Обновление
   */
  public readonly update = 'ttp_economic_agreement.update';

  /**
   * Удаление
   */
  public readonly delete = 'ttp_economic_agreement.delete';

  /**
   * История
   */
  public readonly history = 'ttp_economic_agreement.history';
}

/**
 * Набор разрешений "Отчёты" в модуле ТТП"
 *
 * @extends {PermissionSet}
 */
export class TtpReport extends PermissionSet {
  // Получение XLSX отчетов
  public readonly get = 'ttp_report.get_xlsx';
}

/**
 * Набор разрешений "История событий в ТТП" в модуле ТТП"
 *
 * @extends {PermissionSet}
 */
export class TtpEventHistory extends PermissionSet {
  // Просмотр истории событий в ТТП
  public readonly list = 'ttp_event_histories.list';
}

/**
 * Набор разрешений "Банки" в модуле Справочники"
 *
 * @extends {PermissionSet}
 */
export class Banks extends PermissionSet {
  // Просмотр Банков
  public readonly list = 'banks.list';

  // Редактирование/создание Банков
  public readonly edit = 'banks.edit';
}

/**
 * Набор разрешений "История изменений" в модуле ТТП"
 *
 * @extends {PermissionSet}
 */
export class History extends PermissionSet {
  // Просмотр истории событий в ТТП
  public readonly list = 'ttp_event_histories.list';
}

/**
 * Набор разрешений "Документ удостоверяющий личность" в модуле ТТП"
 *
 * @extends {PermissionSet}
 */
export class IdentityDocument extends PermissionSet {
  /**
   * Просмотр информации
   */
  public readonly list = 'identity_document.list';

  /**
   * Создание
   */
  public readonly create = 'identity_document.create';

  /**
   * Обновление
   */
  public readonly update = 'identity_document.update';

  /**
   * Удаление
   */
  public readonly delete = 'identity_document.delete';

  /**
   * История
   */
  public readonly history = 'identity_document.history';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для значений справочников
 *
 * @export
 * @class catalogValue
 * @extends {PermissionSet}
 */
export class CatalogValue extends PermissionSet {
  /**
   * Просмотр информации о записи в справочнике
   * @memberof catalogValue
   */
  public readonly list = 'catalog_value.list';

  /**
   * Создание записи в справочнике
   * @memberof catalogValue
   */
  public readonly create = 'catalog_value.create';

  /**
   * Обновление информации о записи в справочнике
   * @memberof catalogValue
   */
  public readonly update = 'catalog_value.update';

  /**
   * Удаление записи в справочнике
   * @memberof catalogValue
   */
  public readonly delete = 'catalog_value.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для значений материальных средств
 *
 * @export
 * @extends {PermissionSet}
 */
export class MaterialResource extends PermissionSet {
  /**
   * Просмотр информации о материальных средств
   */
  public readonly list = 'material_resource.list';

  /**
   * Создание записи в справочнике
   */
  public readonly create = 'material_resource.create';

  /**
   * Обновление информации о материальных средств
   */
  public readonly update = 'material_resource.update';

  /**
   * Удаление записи в  материальных средств
   */
  public readonly delete = 'material_resource.delete';
}

/**
 * Набор разрешений для ЕГИССО
 *
 * @export
 * @extends {PermissionSet}
 */
export class EgissoSetting extends PermissionSet {
  /**
   * Обновление информации о ЕГИССО
   */
  public readonly edit = 'egisso_setting.edit';

  /**
   * Просмотр информации о ЕГИССО
   */
  public readonly list = 'egisso_setting.list';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для значений реестра материальных средств
 *
 * @export
 * @extends {PermissionSet}
 */
export class RegistryMaterialResource extends PermissionSet {
  /**
   * Просмотр информации о реестре материальных средств
   */
  public readonly list = 'registry_material_resource.list';

  /**
   * Создание записи в справочнике
   */
  public readonly create = 'registry_material_resource.create';

  /**
   * Обновление информации о реестре материальных средств
   */
  public readonly update = 'registry_material_resource.update';

  /**
   * Удаление записи в реестре материальных средств
   */
  public readonly delete = 'registry_material_resource.delete';

  /**
   * Возможность взаимодействовать с очередью любого департамента
   */
  public readonly forAllDepartment = 'registry_material_resource.for_all_department';

  /**
   * Возможность просматривать историю реестровой записи
   */
  public readonly history = 'registry_material_resource.history';

  /**
   * Возможность выдать мат средства
   */
  public readonly provided = 'registry_material_resource.provided';

  /**
   * Возможность отправить уведомления
   */
  public sendNotification = 'registry_material_resource.send_notification';
}

/**
 * Набор разрешений для работы с НПА в разделе ТМЦ/ЧК
 */
export class RegistryMaterialResourceNpa extends PermissionSet {
  /**
   * Просмотр информации о реестре НПА
   */
  public readonly list = 'act.list';

  /**
   * Создание записи в справочнике
   */
  public readonly create = 'act.create';

  /**
   * Обновление информации о реестре НПА
   */
  public readonly update = 'act.update';

  /**
   * Удаление записи в реестре НПА
   */
  public readonly delete = 'act.delete';

  /**
   * Возможность взаимодействовать с НПА любого уполномоченного орагана
   */
  public readonly forAllDepartment = 'act.for_all_department';

  /**
   * Возможность просматривать историю реестровой записи
   */
  public readonly history = 'act.history';

  /**
   * Возможность утверждать НПА
   */
  public readonly approve = 'act.approve';

  /**
   * Возможность генерации печатной формы НПА
   */
  public readonly generateAct = 'act.generate_act';
}

export class RegistryMaterialResourceDecreeGsm extends PermissionSet {
  /**
   * Просмотр информации о реестре НПА
   */
  public readonly list = 'decree_gsm.list';

  /**
   * Создание записи в справочнике
   */
  public readonly create = 'decree_gsm.create';

  /**
   * Обновление информации о реестре НПА
   */
  public readonly update = 'decree_gsm.update';

  /**
   * Удаление записи в реестре НПА
   */
  public readonly delete = 'decree_gsm.delete';

  /**
   * Возможность взаимодействовать с НПА любого уполномоченного орагана
   */
  public readonly forAllDepartment = 'decree_gsm.for_all_department';

  /**
   * Возможность просматривать историю реестровой записи
   */
  public readonly history = 'decree_gsm.history';

  /**
   * Возможность утверждать НПА
   */
  public readonly approve = 'decree_gsm.approve';

  /**
   * Возможность генерации печатной формы НПА
   */
  public readonly generateAct = 'decree_gsm.generate_act';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для профилей пользователей
 *
 * @export
 * @class userProfile
 * @extends {PermissionSet}
 */
export class UserProfile extends PermissionSet {
  /**
   * Просмотр информации о профиле пользователя
   * @memberof userProfile
   */
  public readonly list = 'user_profile.list';

  /**
   * Создание профиля пользователя
   * @memberof userProfile
   */
  public readonly create = 'user_profile.create';

  /**
   * Обновление информации о профиле пользователя
   * @memberof userProfile
   */
  public readonly update = 'user_profile.update';

  /**
   * Удаление профиля пользователя
   * @memberof userProfile
   */
  public readonly delete = 'user_profile.delete';

  /**
   * Создание профиля пользователя для любого участника системы
   * @memberof userProfile
   */
  public readonly createForAll = 'user_profile.create_for_all';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для полей
 *
 * @export
 * @class field
 * @extends {PermissionSet}
 */
export class Field extends PermissionSet {
  /**
   * Просмотр информации о поле для заполнения
   * @memberof field
   */
  public readonly list = 'field.list';

  /**
   * Создание поля для заполнения
   * @memberof field
   */
  public readonly create = 'field.create';

  /**
   * Обновление информации о поле для заполнения
   * @memberof field
   */
  public readonly update = 'field.update';

  /**
   * Удаление поля для заполнения
   * @memberof field
   */
  public readonly delete = 'field.delete';

  /**
   * Согласование полей, имеется ввиду перевод как в статус _approve_,
   * так и в _reject_.
   * @type {'field.approve'};
   */
  public readonly approve = 'field.change_status';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для типов полей
 *
 * @export
 * @class fieldType
 * @extends {PermissionSet}
 */
export class FieldType extends PermissionSet {
  /**
   * Просмотр информации о типе поля для заполнения
   * @memberof fieldType
   */
  public readonly list = 'field_type';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для секций
 *
 * @export
 * @class section
 * @extends {PermissionSet}
 */
export class Section extends PermissionSet {
  /**
   * Просмотр информации о секции
   * @memberof section
   */
  public readonly list = 'section.list';

  /**
   * Создание секции
   * @memberof section
   */
  public readonly create = 'section.create';

  /**
   * Обновление инофрмации о секции
   * @memberof section
   */
  public readonly update = 'section.update';

  /**
   * Удаление секции
   * @memberof section
   */
  public readonly delete = 'section.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для шагов бизнес-процессов
 *
 * @export
 * @class step
 * @extends {PermissionSet}
 */
export class Step extends PermissionSet {
  /**
   * Просмотр информации о шаге бизнес-процесса
   * @memberof step
   */
  public readonly list = 'step.list';

  /**
   * Создание шага бизнес-процесса
   * @memberof step
   */
  public readonly create = 'step.create';

  /**
   * Обновление шага бизнес-процесса
   * @memberof step
   */
  public readonly update = 'step.update';

  /**
   * Удаление шага бизнес-процесса
   * @memberof step
   */
  public readonly delete = 'step.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для маршрутов
 *
 * @export
 * @class serviceRoute
 * @extends {PermissionSet}
 */
export class ServiceRoute extends PermissionSet {
  /**
   * Просмотр информации о маршруте
   * @memberof serviceRoute
   */
  public readonly list = 'service_route.list';

  /**
   * Смена статуса маршрута
   * @memberof serviceRoute
   */
  public readonly edit = 'service_route.edit';

  /**
   * Публикация маршрута
   * @memberof serviceRoute
   */
  public readonly publish = 'service_route.approve';

  /**
   * Просмотр маршрутов находящихся в разработке
   *
   * @type {string}
   */
  public readonly watchTemplates = 'service_route.watch_templates';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для услуг
 *
 * @export
 * @class service
 * @extends {PermissionSet}
 */
export class Service extends PermissionSet {
  /**
   * Просмотр информации об услуге
   * @memberof service
   */
  public readonly list = 'service.list';

  /**
   * Публикация услуги
   * @memberof service
   */
  public readonly publish = 'service.approve';

  /**
   * Изменения по услуге (CUD)
   *
   * @memberof service
   */
  public readonly edit = 'service.edit';

  /**
   * Просмотр неопубликованных услуг. Базовое разрешение для всех операторов
   *
   * @type {string}
   */
  public readonly watchUnpublished = 'service.watch_unpublished';

  /**
   * Просмотр отчетов по услугам
   *
   * @type {string}
   */
  public readonly reports = 'service.reports';

  /**
   * Просмотр общего отчета по услугам
   *
   * @type {string}
   */
  public readonly generalStatistics = 'service.general_statistics';

  /**
   * Просмотр общего отчета по услугам конкретного департамента
   *
   * @type {string}
   */
  public readonly generalDepartmentStatistics = 'service.general_department_statistics';
}

// noinspection JSUnusedGlobalSymbols
export class Notification extends PermissionSet {
  /**
   * Просмотр истории уведомлений
   * @memberof notification
   */
  public readonly viewHistory = 'notification.view_history';

  /**
   * Повторная отправка недоставленного SMS без создания нового уведомления
   * @memberof notification
   */
  public readonly resendMessage = 'notification.resend_message';
}

// noinspection JSUnusedGlobalSymbols
export class Tag extends PermissionSet {
  /**
   * Просмотр информации о теге для услуг
   * @memberOf service
   */
  public readonly list = 'tag.list';

  /**
   * Создание тега для услуг
   * @memberOf service
   */
  public readonly create = 'tag.create';

  /**
   * Обновление информации о теге для услуг
   * @memberOf service
   */
  public readonly update = 'tag.update';

  /**
   * Удаление тега для услуг
   * @memberOf service
   */
  public readonly delete = 'tag.delete';

  /**
   * Подписка не тег (для получения уведомлений)
   *
   * @type {string}
   */
  public readonly subscription = 'tag.subscription';
}

/**
 * Набор разрешений для групп услуг
 *
 * @export
 * @class serviceGroup
 * @extends {PermissionSet}
 */
// noinspection JSUnusedGlobalSymbols
export class ServiceGroup extends PermissionSet {
  /**
   * Просмотр информации о группе услуг
   * @memberof serviceGroup
   */
  public readonly list = 'service_group.list';

  /**
   * Создание группы услуг
   * @memberof serviceGroup
   */
  public readonly create = 'service_group.create';

  /**
   * Обновление информации о группе услуг
   * @memberof serviceGroup
   */
  public readonly update = 'service_group.update';

  /**
   * Удаление группы услуг
   * @memberof serviceGroup
   */
  public readonly delete = 'service_group.delete';
}

/**
 * Набор разрешений для шаблонов уведомлений
 * Backend группа: template
 *
 * @export
 * @class notificationTemplate
 * @extends {PermissionSet}
 */
export class Template extends PermissionSet {
  /**
   * Просмотр информации о шаблоне уведомлений
   * @memberof notificationTemplate
   */
  public readonly list = 'template.list';

  /**
   * Создание шаблона уведомлений
   * @memberof template
   */
  public readonly create = 'template.create';

  /**
   * Обновление шаблона уведомлений
   * @memberof template
   */
  public readonly update = 'template.update';

  /**
   * Удаление шаблона уведомлений
   * @memberof template
   */
  public readonly delete = 'template.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для заявлений
 *
 * @export
 * @class issue
 * @extends {PermissionSet}
 */
export class Issue extends PermissionSet {
  /**
   * Просмотр информации о заявлении
   * @memberof issue
   */
  public readonly list = 'issue.list';

  public readonly listAll = 'issue.list_all';

  /**
   * Доступ к расширенному набору фильтров
   *
   * @type {string}
   */
  public readonly listFilters = 'issue.list_filters';

  /**
   * Создание заявления
   * @memberof issue
   */
  public readonly create = 'issue.create';

  /**
   * Заполнение заявления
   * @memberof issue
   */
  public readonly fill = 'issue.fill';

  /**
   * Заполнение заявления любого участника системы
   * @memberof issue
   */
  public readonly fillForUser = 'issue.fill_for_user';

  /**
   * Отмена заявления
   * @memberof issue
   */
  public readonly cancel = 'issue.cancel';

  /**
   * Удаление заявления вместе с заявками ТМЦ
   * @memberof issue
   */
  public readonly deleteWithTmc = 'issue.delete_with_tmc';

  /**
   * Просмотр сообщений в чате с заявителем
   *
   * @type {string}
   */
  public readonly chatWatch = 'issue.chat_watch';

  /**
   * Отправка сообщений в чате с заявителем
   *
   * @type {string}
   */
  public readonly chatCommunication = 'issue.chat_communication';

  /**
   * Получение списка ошибок СПГУ
   */
  public readonly epguIssueErrors = 'issue.epgu_issue_errors';

  /**
   * Получение списка ошибок СПГУ
   */
  public readonly epguIssueReject = 'issue.epgu_issue_reject';
}

/**
 * Описания системы
 *
 * @export
 * @class ProjectDescription
 * @extends {PermissionSet}
 */
export class ProjectDescription extends PermissionSet {
  /**
   * Создание/обновление описания системы
   *
   * @memberof ProjectDescription
   */
  public readonly update = 'project_description.update';

  /**
   * Просмотр описания проекта
   * @type {string}
   */
  public readonly list = 'project_description.list';
}

/**
 * Набор разрешения для взаимодействия с производственным календарем
 */
export class TimetableException extends PermissionSet {
  /**
   * Просмотр нерабочих дней
   *
   * @type {string}
   */
  public readonly list = 'timetable_exception.list';

  /**
   * Добавление нерабочих дней/исключение выходных из списка нерабочих дней
   *
   * @type {string}
   */
  public readonly create = 'timetable_exception.create';

  /**
   * Удалению исключений из рабочих/нерабочих дней
   *
   * @type {string}
   */
  public readonly delete = 'timetable_exception.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для взаимодействия со статистикой
 */
export class Statistics extends PermissionSet {
  /**
   * Просмотр статистики
   * @type {string}
   */
  public readonly list = 'statistics.list';

  /**
   * Просмотр личной статистики
   * @type {string}
   */
  public readonly person = 'statistics.person';

  /**
   * Получение статистики по пользователям
   * @type {string}
   */
  public readonly user = 'statistics.user';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для взаимодействия с новостями
 */
export class News extends PermissionSet {
  /**
   * Просмотр
   * @type {string}
   */
  public readonly list = 'news.list';

  /**
   * Создание обращения в техническу поддержку
   * @type {string}
   */
  public readonly create = 'news.create';

  /**
   * Обновление новости
   * @type {string}
   */
  public readonly update = 'news.update';

  /**
   * Удаление новости
   * @type {string}
   */
  public readonly delete = 'news.delete';

  /**
   * Публикация/снятие с публикации новости
   * @type {string}
   */
  public readonly publish = 'news.publish';
}

export class NotificationSmsIntegration extends PermissionSet {
  /**
   * Просмотр настройки СМС-провайдеров
   * @type {string}
   */
  public readonly list = 'notification_sms_integration.list';

  /**
   * Создание настройки СМС-провайдеров
   * @type {string}
   */
  public readonly create = 'notification_sms_integration.create';

  /**
   * Обновление настройки СМС-провайдеров
   * @type {string}
   */
  public readonly update = 'notification_sms_integration.update';

  /**
   * Удаление настройки СМС-провайдеров
   * @type {string}
   */
  public readonly delete = 'notification_sms_integration.delete';
}

export class UsefulLinks extends PermissionSet {
  /**
   * Просмотр полезных ссылок
   * @type {string}
   */
  public readonly list = 'useful_links.list';

  /**
   * Создание полезной ссылки
   * @type {string}
   */
  public readonly create = 'useful_links.create';

  /**
   * Обновление полезной ссылки
   * @type {string}
   */
  public readonly update = 'useful_links.update';

  /**
   * Удаление полезной ссылки
   * @type {string}
   */
  public readonly delete = 'useful_links.delete';

  /**
   * Экспорт полезных ссылок
   * @type {string}
   */
  public readonly export = 'useful_links.export';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для справочника единиц измерения
 *
 * @export
 * @class measure
 * @extends {PermissionSet}
 */
export class MeasureUnit extends PermissionSet {
  /**
   * Просмотр информации о справочнике
   * @memberof measure
   */
  public readonly list = 'measure_unit.list';

  /**
   * Создание справочника
   * @memberof measure
   */
  public readonly create = 'measure_unit.create';

  /**
   * Обновление информации о справочнике
   * @memberof measure
   */
  public readonly update = 'measure_unit.update';

  /**
   * Удаление справочника
   * @memberof measure
   */
  public readonly delete = 'measure_unit.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для работы с обращениями граждан
 *
 * @export
 * @class measure
 * @extends {PermissionSet}
 */
export class Feedback extends PermissionSet {
  /**
   * Просмотр обращений
   * @memberof measure
   */
  public readonly list = 'feedback.list';
}

/**
 * Набор разрешений для работы с заплонированными задачами
 *
 * @export
 * @class SchedulingTasks
 * @extends {PermissionSet}
 */
export class SchedulingTasks extends PermissionSet {
  /**
   * Редактирование заплонированных задач
   * @memberof measure
   */
  public readonly edit = 'scheduling_tasks.edit';
}

/**
 * Набор разрешений для работы с массовой рассылкой
 *
 * @export
 * @class MassNotification
 * @extends {PermissionSet}
 */
export class MassNotification extends PermissionSet {
  /**
   * mass_notifications.list - на get
   * mass_notifications.edit - на все остальное
   * mass_notifications.send - на send
   */

  public readonly list = 'mass_notifications.list';

  public readonly edit = 'mass_notifications.edit';

  public readonly send = 'mass_notifications.send';
}

/**
 * Набор разрешений для работы с синхронизацией с витриной
 *
 * @export
 * @class Nsud
 * @extends {PermissionSet}
 */
export class Nsud extends PermissionSet {
  /**
   * Редактирование заплонированных задач
   * @memberof measure
   */
  public readonly list = 'nsud.list';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для работы с обращениями граждан
 *
 * @export
 * @class integration
 * @extends {PermissionSet}
 */
export class Integration extends PermissionSet {
  /**
   * Просмотр
   * @memberof integration
   */
  public readonly list = 'integration.list';

  /**
   * Создание
   * @memberof integration
   */
  public readonly create = 'integration.create';

  /**
   * Обновление информации
   * @memberof integration
   */
  public readonly update = 'integration.update';

  /**
   * Удаление
   * @memberof integration
   */
  public readonly delete = 'integration.delete';

  public readonly listConfig = 'integration.list_config';

  public readonly binding = 'integration.binding';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для чатов
 */
export class Chat extends PermissionSet {
  /**
   * Просмотр информации о чате
   * @type {string}
   */
  public readonly list = 'chat.list';

  /**
   * Создание нового чата
   * @type {string}
   */
  public readonly create = 'chat.create';

  /**
   * Обновление информации о чате
   * @type {string}
   */
  public readonly update = 'chat.update';

  /**
   * Удаление чата
   * @type {string}
   */
  public readonly delete = 'chat.delete';

  /**
   * Отправка сообщений в чат
   * @type {string}
   */
  public readonly communication = 'chat.communication';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для материально-технических средств
 */
export class MtsType extends PermissionSet {
  /**
   * Просмотр материально-технических средств
   * @type {string}
   */
  public readonly list = 'mts_type.list';

  /**
   * Создание материально-технических средств
   * @type {string}
   */
  public readonly create = 'mts_type.create';

  /**
   * Обновление материально-технических средств
   * @type {string}
   */
  public readonly update = 'mts_type.update';

  /**
   * Удаление материально-технических средств
   * @type {string}
   */
  public readonly delete = 'mts_type.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для национальностей
 */
export class Nation extends PermissionSet {
  /**
   * Просмотр национальностей
   * @type {string}
   */
  public readonly list = 'nation.list';

  /**
   * Создание национальностей
   * @type {string}
   */
  public readonly create = 'nation.create';

  /**
   * Обновление национальностей
   * @type {string}
   */
  public readonly update = 'nation.update';

  /**
   * Удаление национальностей
   * @type {string}
   */
  public readonly delete = 'nation.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Набор разрешений для недропользователей
 * Справочники ТТП
 */
export class SubsurfaceUser extends PermissionSet {
  /**
   * Просмотр недропользователей
   * @type {string}
   */
  public readonly list = 'subsurface_user.list';

  /**
   * Создание недропользователей
   * @type {string}
   */
  public readonly create = 'subsurface_user.create';

  /**
   * Обновление недропользователей
   * @type {string}
   */
  public readonly update = 'subsurface_user.update';

  /**
   * Удаление недропользователей
   * @type {string}
   */
  public readonly delete = 'subsurface_user.delete';
}

// noinspection JSUnusedGlobalSymbols
class Ttp extends PermissionSet {
  /**
   * Просмотр списка ТТП
   * @type {string}
   */
  readonly list = 'ttp.list';

  /**
   * Создание ТТП
   * @type {string}
   */
  readonly create = 'ttp.create';

  /**
   * Редактирование ТТП
   *
   * @type {string}
   */
  readonly update = 'ttp.update';

  /**
   * Удаление ТТП
   * @type {string}
   */
  readonly delete = 'ttp.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Физ. лица ТТП
 */
class Person extends PermissionSet {
  /**
   * Просмотр списка ФЛ ТТП
   * @type {string}
   */
  readonly list = 'person.list';

  /**
   * Добавление физического лица в реестр ТТП
   * @type {string}
   */
  readonly create = 'person.create';

  /**
   * Редкатирование физического лица в реестре ТТП
   * @type {string}
   */
  readonly update = 'person.update';

  /**
   * Удаление физического лица из реестра ТТП
   * @type {string}
   */
  readonly delete = 'person.delete';

  /**
   * Разрешение на исключение физ лица из реестра ТТП
   * @type {string}
   */
  readonly excluded = 'person.excluded';

  /**
   * Разрешение на смену роли физлицу в домохозяйстве
   * @type {string}
   */
  readonly changeRole = 'person.change_role';

  /**
   * Разрешение на просмотр истории
   * @type {string}
   */
  readonly history = 'person.history';

  /**
   * Разрешение на просмотр выгрузки ФЛ для Депздрава ЯНАО
   * @type {string}
   */
  readonly depzravXmlForming = 'person.depzrav_xml_forming';

  /**
   * Разрешение на просмотр выгрузки ФЛ для ИС Соцзащиты
   * @type {string}
   */
  readonly dsznXmlForming = 'person.dszn_xml_forming';

  /**
   * Просмотр сообщений о рождении из ЕГИССО
   */
  readonly egissoBirthList = 'person.egisso_birth_list';

  /**
   * Просмотр сообщений о рождении по всем департаментам
   */
  readonly egissoBirthListAll = 'person.egisso_birth_list_all';

  /**
   * Просмотр сообщений о смерти из ЕГИССО
   */
  readonly egissoDeathList = 'person.egisso_death_list';

  /**
   * Просмотрт сообшщений о смерти из ЕГИССО по всем департаментам
   */
  readonly egissoDeathListAll = 'person.egisso_death_list_all';
}

// noinspection JSUnusedGlobalSymbols
class TtpOrganization extends PermissionSet {
  /**
   * Просмотр организаций в реестре ТТП
   * @type {string}
   */
  readonly list = 'ttp_organization.list';

  /**
   * Создание новой организации в реестре ТТП
   * @type {string}
   */
  readonly create = 'ttp_organization.create';

  /**
   * Обновление организации в реестре ТТП
   * @type {string}
   */
  readonly update = 'ttp_organization.update';

  /**
   * Удаление организации из реестра ТТП
   * @type {string}
   */
  readonly delete = 'ttp_organization.delete';

  /**
   * Исключение организации из реестра ТТП
   * @type {string}
   */
  readonly excluded = 'ttp_organization.excluded';

  /**
   * История изменений
   * @type {string}
   */
  readonly history = 'ttp_organization.history';
}

/**
 * Backend группа: commune
 */
class TtpCommune extends PermissionSet {
  /**
   * Просмотр общины в реестре ТТП
   * @type {string}
   */
  readonly list = 'commune.list';

  /**
   * Создание новой общины в реестре ТТП
   * @type {string}
   */
  readonly create = 'commune.create';

  /**
   * Обновление общины в реестре ТТП
   * @type {string}
   */
  readonly update = 'commune.update';

  /**
   * Удаление общины из реестра ТТП
   * @type {string}
   */
  readonly delete = 'commune.delete';

  /**
   * Исключение общины из реестра ТТП
   * @type {string}
   */
  readonly exclude = 'commune.exclude';
}

/**
 * Backend группа: ttp_deer
 */
class TtpDeer extends PermissionSet {
  /**
   * Просмотр поголовья оленей в реестре ТТП
   * @type {string}
   */
  readonly list = 'ttp_deer.list';

  /**
   * Создание поголовья оленей в реестре ТТП
   * @type {string}
   */
  readonly create = 'ttp_deer.create';

  /**
   * Обновление поголовья оленей в реестре ТТП
   * @type {string}
   */
  readonly update = 'ttp_deer.update';

  /**
   * Удаление поголовья оленей в реестре ТТП
   * @type {string}
   */
  readonly delete = 'ttp_deer.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Выписки из реестра ТТП для физического лица
 */
class PersonExcerptTtp extends PermissionSet {
  /**
   * Просмотр выписки из реестра ТТП для физического лица
   * @type {string}
   */
  readonly list = 'person_excerpt_ttp.list';

  /**
   * Создание выписки из реестра ТТП для физического лица
   * @type {string}
   */
  readonly create = 'person_excerpt_ttp.create';

  /**
   * Изменение выписки из реестра ТТП для физического лица
   * @type {string}
   */
  readonly update = 'person_excerpt_ttp.update';

  /**
   * Удаление выписки из реестра ТТП для физического лица
   * @type {string}
   */
  readonly delete = 'person_excerpt_ttp.delete';

  /**
   * Генерация выписки из реестра ТТП для физического лица по шаблону
   * @type {string}
   */
  readonly export = 'person_excerpt_ttp.export';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Выписки из реестра ТТП для юридического лица
 */
class OrganizationExcerptTtp extends PermissionSet {
  /**
   * Просмотр выписки из реестра ТТП для юридического лица
   * @type {string}
   */
  readonly list = 'organization_excerpt_ttp.list';

  /**
   * Создание выписки из реестра ТТП для юридического лица
   * @type {string}
   */
  readonly create = 'organization_excerpt_ttp.create';

  /**
   * Изменение выписки из реестра ТТП для юридического лица
   * @type {string}
   */
  readonly update = 'organization_excerpt_ttp.update';

  /**
   * Удаление выписки из реестра ТТП для юридического лица
   * @type {string}
   */
  readonly delete = 'organization_excerpt_ttp.delete';

  /**
   * Генерация выписки из реестра ТТП для юридического лица по шаблону
   * @type {string}
   */
  readonly export = 'organization_excerpt_ttp.export';
}

class GovSupportType extends PermissionSet {
  // Просмотр информации
  public readonly list = 'gov_support_type.list';

  // Создание
  public readonly create = 'gov_support_type.create';

  // Обновление
  public readonly update = 'gov_support_type.update';

  // Удаление
  public readonly delete = 'gov_support_type.delete';
}

// noinspection JSUnusedGlobalSymbols
/**
 * Разрешения для формы отчетности
 */
class ReportFormKind extends PermissionSet {
  readonly basic = 'report_form_kind.basic';

  readonly list = 'report_form_kind.list';

  readonly create = 'report_form_kind.create';

  readonly update = 'report_form_kind.update';

  readonly delete = 'report_form_kind.delete';

  // Полное взаимодействие со всеми формами и отчетами
  readonly full = 'report_form_kind.all';
}

/**
 * Заполняемые отчеты по форме
 */
class Report extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'report.list';

  /**
   *
   * @type {string}
   */
  readonly create = 'report.create';

  /**
   *
   * @type {string}
   */
  readonly update = 'report.update';

  /**
   *
   * @type {string}
   */
  readonly delete = 'report.delete';

  /**
   *
   * @type {string}
   */
  readonly approve = 'report.approve';
}

class Organization extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'organization.list';

  /**
   *
   * @type {string}
   */
  readonly create = 'organization.create';

  /**
   *
   * @type {string}
   */
  readonly update = 'organization.update';

  /**
   *
   * @type {string}
   */
  readonly delete = 'organization.delete';

  /**
   *
   * @type {string}
   */
  readonly admin = 'organization.admin';
}

class Household extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'household.list';

  /**
   *
   * @type {string}
   */
  readonly create = 'household.create';

  /**
   *
   * @type {string}
   */
  readonly update = 'household.update';

  /**
   *
   * @type {string}
   */
  readonly delete = 'household.delete';
}

class FadnIntegration extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'fadn_integration.list';

  /**
   *
   * @type {string}
   */
  readonly create = 'fadn_integration.create';
}

class HouseholdRole extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'household_role.list';

  /**
   *
   * @type {string}
   */
  readonly create = 'household_role.create';

  /**
   *
   * @type {string}
   */
  readonly update = 'household_role.update';

  /**
   *
   * @type {string}
   */
  readonly delete = 'household_role.delete';
}

// noinspection JSUnusedGlobalSymbols
class LicenseArea extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'license_area.list';

  /**
   *
   * @type {string}
   */
  readonly importXlsx = 'license_area.import_xlsx';
}

/**
 * Разрешения для сертификатов пользователя
 */
class UserCertificate extends PermissionSet {
  /**
   * Получение сертификатов любого пользователя
   */
  readonly listAll = 'user_certificate.list_all';

  /**
   * Получение своих сертификатов
   */
  readonly list = 'user_certificate.list';

  /**
   * Действия над сертификатами создание/удаление
   */
  readonly edit = 'user_certificate.edit';
}

class MassMedia extends PermissionSet {
  /**
   *
   * @type {string}
   */
  readonly list = 'mass_media.list';

  readonly create = 'mass_media.create';

  readonly update = 'mass_media.update';

  readonly delete = 'mass_media.delete';

  readonly actualize = 'mass_media.actualize';

  readonly exportRegistry = 'mass_media.export_registry';
}

class SedDeloSettings extends PermissionSet {
  readonly list = 'sed_delo_settings.list';

  readonly update = 'sed_delo_settings.update';
}

export class Signing extends PermissionSet {
  /**
   * Подписание документа
   * @type {string}
   */
  public readonly sign = 'signing.sign';

  /**
   * Экспорт открепленной ЭЦП
   * @type {string}
   */
  public readonly export = 'signing.export';
}

class LegacyDocument extends PermissionSet {
  /**
   * Просмотр прочих документов
   */
  readonly list = 'legacy_document.list';
}

/**
 * Разрешения для работы с Directum-like документами
 */
class Documents extends PermissionSet {
  readonly read = 'document.read';

  readonly edit = 'document.edit';

  readonly create = 'document.create';
}

class CommissionApplications extends PermissionSet {
  readonly read = 'application.read';

  readonly edit = 'application.edit';

  readonly delete = 'application.delete';
}

class CommissionMeetings extends PermissionSet {
  readonly read = 'commission_meeting.read';

  readonly edit = 'commission_meeting.edit';

  readonly schedule = 'commission_meeting.schedule';
}

class CommissionDecision extends PermissionSet {
  readonly read = 'commission_decision.read';

  readonly edit = 'commission_decision.edit';

  readonly paragraph = 'commission_decision.paragraph';

  readonly setVote = 'commission_decision.set_vote';

  readonly approve = 'commission_decision.approve';
}

export class GlobalSettings extends PermissionSet {
  public readonly list = 'global_settings.list';

  public readonly edit = 'global_settings.edit';
}

export class NotificationRecipient extends PermissionSet {
  public readonly edit = 'notification_recipient.edit';
}

/**
 * Все доступные разрешения сгруппированные в наборы как беке
 *
 * @class Permissions
 */
export class AvailablePermissions {
  /**
   * Набор разрешений для работы с реестром ТТП
   * @type {Ttp}
   */
  readonly ttp = new Ttp();

  readonly person = new Person();

  readonly ttpDeer = new TtpDeer();

  readonly ttpOrganization = new TtpOrganization();

  readonly commune = new TtpCommune();

  readonly personExcerptTtp = new PersonExcerptTtp();

  readonly organizationExcerptTtp = new OrganizationExcerptTtp();

  /**
   * Набор разрешений для материально-технических средств
   *
   * @memberof AvailablePermissions
   */
  public readonly mtsType = new MtsType();

  public readonly activities = new Activities();

  public readonly messageTemplate = new MessageTemplate();

  public readonly ttpEconomicAgreement = new TtpEconomicAgreement();

  public readonly ttpReport = new TtpReport();

  public readonly ttpEventHistories = new TtpEventHistory();

  public readonly banks = new Banks();

  readonly household = new Household();

  readonly fadnIntegration = new FadnIntegration();

  readonly householdRole = new HouseholdRole();

  readonly licenseArea = new LicenseArea();

  readonly massMedia = new MassMedia();

  readonly sedDeloSettings = new SedDeloSettings();

  readonly identityDocument = new IdentityDocument();

  readonly govSupportType = new GovSupportType();

  /**
   * Набор разрешений для национальностей
   *
   * @memberof AvailablePermissions
   */
  public readonly nation = new Nation();

  /**
   * Набор разрешений для недропользователей
   *
   * @memberof AvailablePermissions
   */
  public readonly subsurfaceUser = new SubsurfaceUser();

  /**
   * Набор разрешений для ролей
   *
   * @memberof AvailablePermissions
   */
  public readonly role = new Role();

  /**
   * Набор разрешений для пользователей
   *
   * @memberof AvailablePermissions
   */
  public readonly user = new User();

  /**
   * Набор разрешений для департаментов
   *
   * @memberof AvailablePermissions
   */
  public readonly department = new Department();

  /**
   * Набор разрешений для файлов
   *
   * @memberof AvailablePermissions
   */
  public readonly file = new File();

  /**
   * Набор разрешений для справочников
   *
   * @memberof AvailablePermissions
   */
  public readonly catalog = new Catalog();

  /**
   * Набор разрешений для значений справочников
   *
   * @memberof AvailablePermissions
   */
  public readonly catalogValue = new CatalogValue();

  /**
   * Набор разрешений для уведомлений
   *
   * @memberof AvailablePermissions
   */
  public readonly notification = new Notification();

  /**
   * Набор разрешений для профилей пользователей
   *
   * @memberof AvailablePermissions
   */
  public readonly userProfile = new UserProfile();

  /**
   * Набор разрешений для полей
   *
   * @memberof AvailablePermissions
   */
  public readonly field = new Field();

  /**
   * Набор разрешений для типов полей
   *
   * @memberof AvailablePermissions
   */
  public readonly fieldType = new FieldType();

  /**
   * Набор разрешений для секций
   *
   * @memberof AvailablePermissions
   */
  public readonly section = new Section();

  /**
   * Набор разрешений для шагов бизнес-процессов
   *
   * @memberof AvailablePermissions
   */
  public readonly step = new Step();

  /**
   * Набор разрешений для маршрутов
   *
   * @memberof AvailablePermissions
   */
  public readonly serviceRoute = new ServiceRoute();

  /**
   * Набор разрешений для услуг
   *
   * @memberof AvailablePermissions
   */
  public readonly service = new Service();

  /**
   * Набор разрешений для тегов услуг
   *
   * @memberof AvailablePermissions
   */
  public readonly tag = new Tag();

  /**
   * Набор разрешений для групп услуг
   *
   * @memberof AvailablePermissions
   */
  public readonly serviceGroup = new ServiceGroup();

  /**
   * Набор разрешений для шаблонов уведомлений
   *
   * @memberof AvailablePermissions
   */
  public readonly template = new Template();

  /**
   * Набор разрешений для заявлений
   *
   * @memberof AvailablePermissions
   */
  public readonly issue = new Issue();

  /**
   * Набор разрешений для работы с описанием системы
   *
   * @memberof AvailablePermissions
   */
  public readonly projectDescription = new ProjectDescription();

  /**
   * Набор разрешения для взаимодействия с производственным календарем
   *
   * @type {TimetableException}
   */
  public readonly timetableException = new TimetableException();

  /**
   * Набор разрешения для взаимодействия с сертификатами пользователя
   *
   * @memberof AvailablePermissions
   */
  public readonly userCertificate = new UserCertificate();

  /**
   * Набор разрешений для взаимодействия со статистикой
   * @type {Statistics}
   */
  public readonly statistics = new Statistics();

  /**
   * Набор разрешений для взаимодействия с новостями
   * @type {News}
   */
  public readonly news = new News();

  /**
   * Набор разрешений для взаимодействия с полезными ссылками
   * @type {UsefulLinks}
   */
  public readonly usefulLinks = new UsefulLinks();

  /**
   * Набор разрешений для взаимодействия с единицами измерения
   *
   * @type {MeasureUnit}
   */
  public readonly measureUnit = new MeasureUnit();

  /**
   * Набор разрешений для Настройки СМС-провайдеров
   *
   * @type {MeasureUnit}
   */
  public readonly notificationSmsIntegration = new NotificationSmsIntegration();

  /**
   * Набор разрешений для работы с обращениями граждан
   *
   * @type {Feedback}
   */
  public readonly feedback = new Feedback();

  /**
   * Набор разрешений для работы с Администрированием интеграций
   *
   * @type {Integration}
   */
  public readonly integration = new Integration();

  /**
   * Набор разрешений для работы с заплонированными задачами
   *
   * @type {SchedulingTasks}
   */
  public readonly schedulingTasks = new SchedulingTasks();

  /**
   * Набор разрешений для работы с массовой рассылкой
   *
   * @type {SchedulingTasks}
   */
  public readonly massNotification = new MassNotification();

  /**
   * Набор разрешений для работы с синхронизацией с витриной
   *
   * @type {Nsud}
   */
  public readonly nsud = new Nsud();

  /**
   * Набор разрешений для чатов
   * @type {Chat}
   */
  public readonly chat = new Chat();

  /**
   * Набор разрешений для значений реестра материальных средств
   * @type {RegistryMaterialResource}
   */
  public readonly registryMaterialResource = new RegistryMaterialResource();

  /**
   * Набор разрешений для значений материальных средств
   * @type {MaterialResource}
   */
  public readonly materialResource = new MaterialResource();

  /**
   * Набор разрешений для ЕГИССО
   * @type {EgissoSetting}
   */
  public readonly egissoSetting = new EgissoSetting();

  /**
   * Набор разрешений для значений реестра НПА ТМЦ/ЧК
   * @type {RegistryMaterialResource}
   */
  public readonly act = new RegistryMaterialResourceNpa();

  /**
   * Набор разрешения для работы с распоряжениями на выплату ГСМ
   */
  public readonly decreeGsm = new RegistryMaterialResourceDecreeGsm();

  /**
   * Набор разрешений для глобальных настроек системы
   * @type {GlobalSettings}
   */
  public readonly globalSettings = new GlobalSettings();

  /**
   * Набор разрешений для получателей уведомлений
   * @type {NotificationRecipient}
   */
  public readonly notificationRecipient = new NotificationRecipient();

  /**
   * Набор разрешений форм отчетности
   * @type {ReportFormKind}
   */
  public readonly reportFormKind = new ReportFormKind();

  readonly report = new Report();

  readonly organization = new Organization();

  /**
   * Набор разрешений для Подписей
   * @type {Chat}
   */
  public readonly signing = new Signing();

  public readonly legacyDocument = new LegacyDocument();

  readonly documents = new Documents();

  readonly application = new CommissionApplications();

  readonly commissionMeeting = new CommissionMeetings();

  readonly commissionDecision = new CommissionDecision();

  /**
   *
   * @returns {this}
   */

  constructor() {
    // eslint-disable-next-line no-constructor-return -- Proxy wrapper for permission sets
    return new Proxy(this, {
      get: (origin, name) => {
        if (origin.getExistingPermissionSets().includes(String(name)) || name === '__v_isRef') {
          return origin[String(name)];
        }
        throw new PermissionSetDoesNotExist(String(name));
      },
    });
  }

  /**
   * Возвращаем массив доступных наборов разрешений
   *
   * @protected
   * @returns
   * @memberof AvailablePermissions
   */
  protected getExistingPermissionSets() {
    return Object.getOwnPropertyNames(this);
  }
}

/**
 * Все доступные разрешения сгруппированные в наборы как беке
 */
export default new AvailablePermissions();
